import { assignSafe, guidRegExPattern } from '@summit/core';

import { BasicListItem } from './models';
import { ToCamelCase } from './string-functions';

export function ToCamelCaseProperties(input: string): string | null {
  if (input === null) {
    return null;
  }
  if (typeof input !== 'object') {
    return input;
  }
  if (Array.isArray(input)) {
    return input;
  }
  return Object.keys(input).reduce(function (newObj: any, key) {
    const val = input[key];
    const newVal = typeof val === 'object' ? ToCamelCaseProperties(val) : val;
    const newKey = ToCamelCase(key);
    // console.log(`Converting ${key} to ${newKey}`);
    newObj[newKey] = newVal;
    return newObj;
  }, {});
}

export function GetSimpleHash(object: unknown): number {
  return JSON.stringify(object)
    .split('')
    .reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
}

export function intersect<T>(input1: Array<T>, input2: Array<T>): Array<T> {
  if (!input1 || !input2) {
    return new Array<T>();
  }
  return input1.filter(function (n) {
    return input2.indexOf(n) > -1;
  });
}

export function isNumber(x: unknown): x is number {
  return typeof x === 'number' || x === 0;
}

export function isGuid(x: unknown): boolean {
  // test value = f320cd05-05c6-4af7-978e-4bd2650b9d19
  return typeof x === 'string' && x.search(guidRegExPattern) >= 0;
}

export function isBoolean(x: unknown): x is boolean {
  return typeof x === 'boolean';
}

export function isJson(x: string): x is string {
  const tryString = x.trim();
  return tryString[0] === '{' || tryString[0] === '[';
}

export function ConvertBitwiseEnumToArray(
  mapping: Record<number, string>,
  filter: number,
  zeroFilterIsNoFilter?: boolean,
  includeNoSelection?: boolean
): BasicListItem[] {
  zeroFilterIsNoFilter ??= true;
  includeNoSelection ??= false;
  const response = Object.keys(mapping)
    .filter((key: any) => (zeroFilterIsNoFilter === true && filter === 0) || (key & filter) > 0)
    .map((key) => {
      // console.log(`Processing ${mapping[key]} = ${key}`);
      return new Object({
        id: +key,
        value: mapping[key as any] as string
      });
    });
  /*    if (!includeNoSelection && response?.length > 0) {
            const index = response.findIndex(x => x['id'] === 0)
            if (index >= 0) {
                response.splice(index, 1);
            }
        }
        */
  if (includeNoSelection) {
    response.unshift(
      new Object({
        id: 0,
        value: mapping[0]
      })
    );
  }
  // console.log(`response = ${response?.map(x => x['value']).join(',')}`)
  return response as BasicListItem[];
}

export function ConvertBitwiseEnumToString(
  mapping: Record<number, string>,
  filter: number,
  zeroFilterIsNoFilter?: boolean,
  includeNoSelection?: boolean
): string {
  zeroFilterIsNoFilter ??= true;
  includeNoSelection ??= false;
  const response = Object.keys(mapping)
    .filter((key: any) => (zeroFilterIsNoFilter === true && filter === 0) || (key & filter) > 0)
    .map((key) => mapping[key as any]);
  if (includeNoSelection) {
    response.unshift(mapping[0]);
  }
  // console.log(`response = ${response.join(',')}`)
  return response.join(', ');
}

export function isObject(item: unknown) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export function assignDeepWithCount(target: any, source: any, modifyCount = 0): [unknown, number] {
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          if (target[key] !== source[key]) {
            assignSafe(target, { [key]: source[key] });
            modifyCount++;
          }
        } else {
          [target[key], modifyCount] = assignDeepWithCount(target[key], source[key], modifyCount);
        }
      } else {
        if (target[key] !== source[key]) {
          assignSafe(target, { [key]: source[key] });
          modifyCount++;
        }
      }
    });
  }
  return [target, modifyCount];
}

export function assignDeepForImport(target: any, source: any, modifyCount = 0): [any, number] {
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          if (target[key] !== source[key]) {
            assignSafe(target, { [key]: source[key] });
            //console.log(`assignDeepWithCount: ${key} = ${source[key]}`);
            modifyCount++;
          }
        } else {
          [target[key], modifyCount] = assignDeepWithCount(target[key], source[key], modifyCount);
        }
      } else {
        if (target[key] !== source[key] && target[key]?.toString() !== source[key]?.toString()) {
          //console.log(`assignDeepWithCount non-object: ${key} = ${source[key]}`);
          assignSafe(target, { [key]: source[key] });
          modifyCount++;
        }
      }
    });
  }
  return [target, modifyCount];
}

export default function mergeDeep(target: any, source: any) {
  const output = assignSafe({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) assignSafe(output, { [key]: source[key] });
        else output[key] = mergeDeep(target[key], source[key]);
      } else {
        assignSafe(output, { [key]: source[key] });
      }
    });
  }
  return output;
}
